<script setup lang="ts">
import {
  selectContentAction,
  selectContentEvent
} from "~/utils/analytics/selectContent"
import { BRANDS_MODAL, CATEGORIES_MODAL, FILTERS_MODAL } from "~/utils/filters"

const { switchState: openFilters } = useUIState(FILTERS_MODAL)
const { switchState: openBrands } = useUIState(BRANDS_MODAL)
const { switchState: openCategories } = useUIState(CATEGORIES_MODAL)
const { t } = useI18n()
const route = useRoute()

const facetNames = ["inStock", "deductibleProduct"]

const { event, sendEvent } = useGAEvent(
  `track_select_content_available_`,
  "select_content"
)

const onClickedFilter = (item: string, category: string) => {
  if (item) {
    event.value = {
      event: selectContentEvent.selectContent,
      action: selectContentAction.filter,
      category: category.toLowerCase(),
      label: t(`filters.utils.availability.${item}`)
    }

    sendEvent({ clearEcommerce: false })
  }
}

const { nbHits, refinements } = useSwiftSearchHelper()
</script>

<template>
  <FiltersUtilsModalWrapper
    :name="FILTERS_MODAL"
    :header="{
      title: $t('filters.utils.modalHead.filter')
    }"
    @onClose="openFilters(false)"
  >
    <div class="filters-modal__form flex flex-grow flex-col px-4">
      <FiltersUtilsModalContent
        :btn="{
          type: 'submit',
          text: $t('filters.utils.modals.filters.showResults'),
          nResults: 120
        }"
        :btn-shown="true"
      >
        <AlgoliaGroupsPrice />

        <FiltersUtilsGroup
          v-if="route.meta.page !== 'promotions-brand'"
          type="modal"
          name="brands"
          id-facet="brand.name"
          :title="$t('filters.utils.common.brands')"
          facetName="brand.name"
          @onClick="openBrands(true)"
        >
          <AisCurrentRefinements
            id="brand.name"
            :included-attributes="['brand.name']"
            :class-names="{
              'ais-CurrentRefinements-item': 'flex flex-wrap gap-x-4',
              'ais-CurrentRefinements-label': 'hidden',
              'ais-CurrentRefinements-delete': 'ml-2 cursor-pointer',
              'ais-CurrentRefinements-category':
                'whitespace-normal bg-green-10 rounded-[100px] px-3 py-2 gap-x-2 mb-4 cursor-default'
            }"
          />
          <template #modal>
            <FiltersUtilsModalsBrands />
          </template>
        </FiltersUtilsGroup>

        <FiltersUtilsGroup
          type="modal"
          name="categories"
          id-facet="mainCategory.name"
          :title="$t('filters.utils.common.categories')"
          facetName="mainCategory.name"
          @onClick="openCategories(true)"
        >
          <AisCurrentRefinements
            id="mainCategory.name"
            :included-attributes="['mainCategory.name']"
            :class-names="{
              'ais-CurrentRefinements-item': 'flex flex-wrap gap-x-4',
              'ais-CurrentRefinements-label': 'hidden',
              'ais-CurrentRefinements-delete': 'ml-2 cursor-pointer',
              'ais-CurrentRefinements-category':
                'whitespace-normal bg-green-10 rounded-[100px] px-3 py-2 gap-x-2 mb-4 cursor-default'
            }"
          />
          <template #modal>
            <FiltersUtilsModalsCategories />
          </template>
        </FiltersUtilsGroup>

        <FiltersUtilsGroup
          name="availability"
          id-facet="availability"
          content-class="pb-4 mb-16"
          :title="$t('filters.utils.common.availability')"
          :facetName="facetNames"
        >
          <AisToggleRefinement
            v-for="(item, index) in facetNames"
            :key="`refinement-toggle-${index}`"
            :attribute="facetNames[index]"
            :label="$t(`filters.utils.availability.${item}`)"
          >
            <template v-slot="{ canRefine, refine, value }">
              <FiltersUtilsOption
                :name="item"
                :checked="value.isRefined"
                :disabled="!canRefine"
                @clicked="
                  () => {
                    onClickedFilter(
                      item,
                      $t(`filters.utils.common.availability`)
                    )
                    refine(value)
                  }
                "
                :text="$t(`filters.utils.availability.${item}`)"
                :n-results="canRefine ? value.count : 0"
                type="toggle"
              />
            </template>
          </AisToggleRefinement>
        </FiltersUtilsGroup>
        <div
          class="
            elevation-fixed-button
            fixed
            bottom-0
            left-0
            right-0
            w-full
            bg-white
            p-4
          "
        >
          <UtilsButton
            @click="openFilters(false)"
            :text="
              $t('filters.utils.common.showResults', {
                number: nbHits ?? 0
              })
            "
            class="w-full"
          />
        </div>
      </FiltersUtilsModalContent>
    </div>
  </FiltersUtilsModalWrapper>
</template>

<style lang="scss">

.elevation-fixed-button {
  box-shadow: 0px -2px 12px 0px rgba(0, 0, 0, 0.08);
  -webkit-overflow-scrolling: touch;
}

.ais-CurrentRefinements-item {
  @apply flex flex-wrap gap-x-4;
}

.ais-CurrentRefinements-label {
  @apply hidden;
}
.ais-CurrentRefinements-delete {
  @apply ml-2 cursor-pointer;
}
.ais-CurrentRefinements-category {
  @apply mb-4 cursor-default gap-x-2 whitespace-normal rounded-[100px] bg-green-10 px-3 py-2;
}

</style>
